<template>
  <v-dialog :value="value" max-width="520" @input="$emit('input', $event)">
    <v-card class="shipment-combine-dialog">
      <v-card-title v-if="result"
        >Combined into Shipment #{{ result.keepId }}</v-card-title
      >
      <v-card-title v-else
        >Combine {{ selectedIds.length }} shipments</v-card-title
      >
      <v-card-text v-if="result" class="combine-result">
        <v-alert
          v-if="result.warning"
          dense
          outlined
          type="warning"
          class="mb-3 combine-warning"
        >
          {{ result.warning }}
        </v-alert>
        <p v-if="result.selectionText" class="mb-2">
          {{ result.selectionText }}
        </p>
        <v-simple-table v-if="result.rates.length" dense>
          <thead>
            <tr>
              <th>Service</th>
              <th class="text-right">Rate</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="rate in result.rates"
              :key="rate.id"
              :class="{ 'combine-rate-selected': rate.selected }"
            >
              <td>
                <v-icon v-if="rate.selected" small color="primary"
                  >mdi-check-circle</v-icon
                >
                {{ rate.name }}
              </td>
              <td class="text-right">
                {{
                  rate.rate === null || rate.rate === undefined
                    ? "-"
                    : $n(rate.rate, "currency")
                }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card-text>
      <v-card-text v-else>
        <v-alert
          v-if="state.reasons.length"
          dense
          outlined
          type="error"
          class="mb-3"
        >
          <div v-for="reason in state.reasons" :key="reason">{{ reason }}</div>
        </v-alert>
        <template v-if="state.preview">
          <p class="mb-2">
            Everything moves into
            <strong>Shipment #{{ state.preview.keepId }}</strong
            >; the others are deleted.
            <strong>{{ state.preview.units }}</strong> units total.
          </p>
          <BoxSelect v-model="form.box" dense />
          <v-row dense>
            <v-col cols="3">
              <v-text-field
                v-model.number="form.weight"
                label="Weight (lb)"
                type="number"
                dense
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model.number="form.length"
                label="Length"
                type="number"
                dense
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model.number="form.width"
                label="Width"
                type="number"
                dense
              />
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model.number="form.height"
                label="Height"
                type="number"
                dense
              />
            </v-col>
          </v-row>
          <p class="text-caption mb-0">
            Defaults: summed weight, largest length/width, stacked height. Rates
            are refreshed automatically.
          </p>
        </template>
      </v-card-text>
      <v-card-actions v-if="result">
        <v-spacer />
        <v-btn color="primary" text @click="$emit('input', false)">Done</v-btn>
      </v-card-actions>
      <v-card-actions v-else>
        <v-spacer />
        <v-btn text @click="$emit('input', false)">Cancel</v-btn>
        <v-btn
          color="primary"
          :disabled="!canSubmit"
          :loading="loading"
          @click="submit"
        >
          Combine
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import jsonApi from "../../plugins/devour";
import BoxSelect from "./BoxSelect.vue";
import { combineResult, combineState } from "@/utils/combine.mjs";

export default {
  name: "ShipmentCombineDialog",
  components: { BoxSelect },
  props: {
    value: { type: Boolean, default: false },
    order: { type: Object, default: null },
    selectedIds: { type: Array, default: () => [] },
  },
  data() {
    return {
      loading: false,
      result: null,
      form: { box: "", weight: 0, length: 0, width: 0, height: 0 },
    };
  },
  computed: {
    state() {
      return combineState(this.order, this.selectedIds);
    },
    canSubmit() {
      const f = this.form;
      return (
        this.state.canCombine &&
        !!f.box &&
        [f.weight, f.length, f.width, f.height].every((n) => Number(n) > 0)
      );
    },
  },
  watch: {
    value(open) {
      if (open) this.result = null;
      if (open && this.state.preview) {
        const p = this.state.preview;
        this.form = {
          box: p.box,
          weight: p.weight,
          length: p.length,
          width: p.width,
          height: p.height,
        };
      }
    },
  },
  methods: {
    async submit() {
      let confirmBand = false;
      if (this.state.hasBand) {
        confirmBand = await this.$confirm(
          "These shipments contain banded items (BAND). Combining will change the band. Continue?",
          {
            title: "Warning",
            buttonTrueText: "Continue",
            buttonFalseText: "Cancel",
          }
        );
        if (!confirmBand) return;
      }

      this.loading = true;
      try {
        const keepId = this.state.preview.keepId;
        const response = await jsonApi.request(
          jsonApi.apiUrl + "/shipments/combine",
          "POST",
          null,
          {
            type: "shipments",
            attributes: {
              shipmentIds: this.selectedIds.map((id) => parseInt(id, 10)),
              ...this.form,
              confirmBand,
            },
          }
        );
        this.result = {
          keepId,
          ...combineResult(response && response.meta),
        };
        this.$bus.$emit("Message", "Shipments combined");
        // Parent reloads the order now; the dialog stays open on the result
        // (fresh rates or the refresh warning) until the packer clicks Done.
        this.$emit("combined", keepId);
      } catch (e) {
        this.$bus.$emit("Error", e);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>
